import React, { useState, useEffect } from 'react';
import { Card, Timeline, Tag, List, Typography, Empty, Spin, Row, Col } from 'antd';
import { CalendarOutlined, FolderOutlined, TagOutlined } from '@ant-design/icons';
import { api } from '../../api/mockData';
import './index.css';

const { Title, Text } = Typography;

const Archive = () => {
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchArchiveData();
  }, []);

  const fetchArchiveData = async () => {
    setLoading(true);
    try {
      const [archivesData, categoriesData, tagsData] = await Promise.all([
        api.getArchives(),
        api.getCategories(),
        api.getTags()
      ]);
      
      setArchives(archivesData);
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (error) {
      console.error('获取归档数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', 
                     '七月', '八月', '九月', '十月', '十一月', '十二月'];

  if (loading) {
    return (
      <div className="spin-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="archive-page">
      <div className="archive-header">
        <Title level={2} className="page-title">
          <CalendarOutlined /> 文章归档
        </Title>
        <Text type="secondary">共 {archives.reduce((sum, item) => sum + item.count, 0)} 篇文章</Text>
      </div>

      <Row gutter={[32, 32]}>
        {/* 时间线归档 */}
        <Col xs={24} lg={16}>
          <Card className="archive-section" title="时间线归档">
            {archives.length > 0 ? (
              <Timeline mode="alternate" className="archive-timeline">
                {archives.map((archive, index) => (
                  <Timeline.Item
                    key={`${archive.year}-${archive.month}`}
                    color={index % 2 === 0 ? 'blue' : 'green'}
                    dot={<CalendarOutlined />}
                  >
                    <div className="timeline-item">
                      <Title level={4} className="timeline-date">
                        {archive.year}年 {monthNames[archive.month - 1]}
                      </Title>
                      <div className="timeline-content">
                        <Tag color="processing" className="article-count">
                          {archive.count} 篇文章
                        </Tag>
                        <Text type="secondary" className="view-link">
                          查看详情 →
                        </Text>
                      </div>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <Empty description="暂无归档数据" />
            )}
          </Card>
        </Col>

        {/* 分类统计 */}
        <Col xs={24} lg={8}>
          <Card className="archive-section" title={
            <span><FolderOutlined /> 分类统计</span>
          }>
            <List
              dataSource={categories}
              renderItem={(category) => (
                <List.Item className="category-item">
                  <div className="category-info">
                    <Text strong>{category.name}</Text>
                    <Text type="secondary">共 {category.count} 篇文章</Text>
                  </div>
                  <div className="category-bar">
                    <div 
                      className="category-progress"
                      style={{ 
                        width: `${(category.count / Math.max(...categories.map(c => c.count))) * 100}%` 
                      }}
                    />
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: '暂无分类' }}
            />
          </Card>

          {/* 标签云 */}
          <Card className="archive-section" title={
            <span><TagOutlined /> 热门标签</span>
          } style={{ marginTop: 24 }}>
            <div className="tag-cloud">
              {tags.map(tag => (
                <Tag 
                  key={tag.id}
                  className="archive-tag"
                  color={getTagColor(tag.count)}
                >
                  {tag.name} ({tag.count})
                </Tag>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// 根据标签数量生成颜色
const getTagColor = (count) => {
  if (count > 15) return 'red';
  if (count > 10) return 'orange';
  if (count > 5) return 'green';
  return 'blue';
};

export default Archive;